import React, { useState, useEffect, useCallback } from 'react';
import { 
  Plus, Edit2, Trash2, Save, X, Upload, Package, 
  Monitor, CheckCircle, XCircle, GripVertical, Star,
  Image as ImageIcon
} from 'lucide-react';
import AdminLayout from './AdminLayout';
import { useAdminAuth } from '../../contexts/AdminAuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDigitalServices = () => {
  const { adminToken: token } = useAdminAuth();
  const [activeTab, setActiveTab] = useState('services');
  
  // Services state
  const [services, setServices] = useState([]);
  const [loadingServices, setLoadingServices] = useState(true);
  const [editingService, setEditingService] = useState(null);
  const [showServiceModal, setShowServiceModal] = useState(false);
  
  // Packages state
  const [packages, setPackages] = useState([]);
  const [loadingPackages, setLoadingPackages] = useState(true);
  const [editingPackage, setEditingPackage] = useState(null);
  const [showPackageModal, setShowPackageModal] = useState(false);
  
  // Form states
  const [serviceForm, setServiceForm] = useState({
    name: '',
    description: '',
    short_description: '',
    price: '',
    image_url: '',
    category: '',
    is_active: true,
    sort_order: 0
  });
  
  const [packageForm, setPackageForm] = useState({
    name: '',
    description: '',
    price: '',
    original_price: '',
    services: [],
    highlight: false,
    badge: '',
    color: '#3B82F6',
    is_active: true,
    sort_order: 0
  });

  const [uploading, setUploading] = useState(false);

  // Categories predefinite
  const categories = [
    'Branding & Marchi',
    'Web & Domini',
    'Grafica & Design',
    'Marketing & Social',
    'E-commerce',
    'Assistenza'
  ];

  // Fetch services
  const fetchServices = useCallback(async () => {
    try {
      setLoadingServices(true);
      const res = await fetch(`${API_URL}/api/admin/digital-services`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setServices(data);
      }
    } catch (error) {
      console.error('Error fetching services:', error);
    } finally {
      setLoadingServices(false);
    }
  }, [token]);

  // Fetch packages
  const fetchPackages = useCallback(async () => {
    try {
      setLoadingPackages(true);
      const res = await fetch(`${API_URL}/api/admin/digital-packages`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setPackages(data);
      }
    } catch (error) {
      console.error('Error fetching packages:', error);
    } finally {
      setLoadingPackages(false);
    }
  }, [token]);

  useEffect(() => {
    fetchServices();
    fetchPackages();
  }, [fetchServices, fetchPackages]);

  // ============== SERVICE HANDLERS ==============

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const res = await fetch(`${API_URL}/api/admin/digital-services/upload-image`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      if (res.ok) {
        const data = await res.json();
        setServiceForm(prev => ({ ...prev, image_url: data.image_url }));
      } else {
        alert('Errore upload immagine');
      }
    } catch (error) {
      console.error('Upload error:', error);
      alert('Errore upload immagine');
    } finally {
      setUploading(false);
    }
  };

  const openServiceModal = (service = null) => {
    if (service) {
      setEditingService(service);
      setServiceForm({
        name: service.name || '',
        description: service.description || '',
        short_description: service.short_description || '',
        price: service.price?.toString() || '',
        image_url: service.image_url || '',
        category: service.category || '',
        is_active: service.is_active !== false,
        sort_order: service.sort_order || 0
      });
    } else {
      setEditingService(null);
      setServiceForm({
        name: '',
        description: '',
        short_description: '',
        price: '',
        image_url: '',
        category: '',
        is_active: true,
        sort_order: services.length
      });
    }
    setShowServiceModal(true);
  };

  const saveService = async () => {
    if (!serviceForm.name || !serviceForm.price) {
      alert('Nome e prezzo sono obbligatori');
      return;
    }

    const payload = {
      ...serviceForm,
      price: parseFloat(serviceForm.price),
      sort_order: parseInt(serviceForm.sort_order) || 0
    };

    try {
      const url = editingService
        ? `${API_URL}/api/admin/digital-services/${editingService.id}`
        : `${API_URL}/api/admin/digital-services`;
      
      const res = await fetch(url, {
        method: editingService ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        setShowServiceModal(false);
        fetchServices();
      } else {
        alert('Errore salvataggio servizio');
      }
    } catch (error) {
      console.error('Save error:', error);
      alert('Errore salvataggio servizio');
    }
  };

  const deleteService = async (id) => {
    if (!window.confirm('Eliminare questo servizio?')) return;

    try {
      const res = await fetch(`${API_URL}/api/admin/digital-services/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        fetchServices();
      } else {
        alert('Errore eliminazione servizio');
      }
    } catch (error) {
      console.error('Delete error:', error);
    }
  };

  // ============== PACKAGE HANDLERS ==============

  const openPackageModal = (pkg = null) => {
    if (pkg) {
      setEditingPackage(pkg);
      setPackageForm({
        name: pkg.name || '',
        description: pkg.description || '',
        price: pkg.price?.toString() || '',
        original_price: pkg.original_price?.toString() || '',
        services: pkg.services || [],
        highlight: pkg.highlight || false,
        badge: pkg.badge || '',
        color: pkg.color || '#3B82F6',
        is_active: pkg.is_active !== false,
        sort_order: pkg.sort_order || 0
      });
    } else {
      setEditingPackage(null);
      setPackageForm({
        name: '',
        description: '',
        price: '',
        original_price: '',
        services: [],
        highlight: false,
        badge: '',
        color: '#3B82F6',
        is_active: true,
        sort_order: packages.length + 1
      });
    }
    setShowPackageModal(true);
  };

  const toggleServiceInPackage = (service, included) => {
    setPackageForm(prev => {
      const existing = prev.services.find(s => s.service_id === service.id);
      
      if (existing) {
        // Update existing
        return {
          ...prev,
          services: prev.services.map(s => 
            s.service_id === service.id 
              ? { ...s, included } 
              : s
          )
        };
      } else {
        // Add new
        return {
          ...prev,
          services: [...prev.services, {
            service_id: service.id,
            service_name: service.name,
            included,
            custom_price: null
          }]
        };
      }
    });
  };

  const getServiceInclusionStatus = (serviceId) => {
    const item = packageForm.services.find(s => s.service_id === serviceId);
    return item?.included ?? false;
  };

  const savePackage = async () => {
    if (!packageForm.name || !packageForm.price) {
      alert('Nome e prezzo sono obbligatori');
      return;
    }

    const payload = {
      ...packageForm,
      price: parseFloat(packageForm.price),
      original_price: packageForm.original_price ? parseFloat(packageForm.original_price) : null,
      sort_order: parseInt(packageForm.sort_order) || 0
    };

    try {
      const url = editingPackage
        ? `${API_URL}/api/admin/digital-packages/${editingPackage.id}`
        : `${API_URL}/api/admin/digital-packages`;
      
      const res = await fetch(url, {
        method: editingPackage ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        setShowPackageModal(false);
        fetchPackages();
      } else {
        alert('Errore salvataggio pacchetto');
      }
    } catch (error) {
      console.error('Save error:', error);
      alert('Errore salvataggio pacchetto');
    }
  };

  const deletePackage = async (id) => {
    if (!window.confirm('Eliminare questo pacchetto?')) return;

    try {
      const res = await fetch(`${API_URL}/api/admin/digital-packages/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        fetchPackages();
      } else {
        alert('Errore eliminazione pacchetto');
      }
    } catch (error) {
      console.error('Delete error:', error);
    }
  };

  // ============== RENDER ==============

  return (
    <AdminLayout>
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Servizi Digital</h1>
            <p className="text-gray-600">Gestisci servizi e pacchetti digitali</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setActiveTab('services')}
            className={`px-4 py-2 rounded-lg font-medium flex items-center gap-2 transition-colors ${
              activeTab === 'services'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <Monitor className="w-4 h-4" />
            Servizi ({services.length})
          </button>
          <button
            onClick={() => setActiveTab('packages')}
            className={`px-4 py-2 rounded-lg font-medium flex items-center gap-2 transition-colors ${
              activeTab === 'packages'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <Package className="w-4 h-4" />
            Pacchetti ({packages.length})
          </button>
        </div>

        {/* Services Tab */}
        {activeTab === 'services' && (
          <div className="bg-white rounded-lg shadow">
            <div className="p-4 border-b flex justify-between items-center">
              <h2 className="font-semibold text-gray-800">Elenco Servizi</h2>
              <button
                onClick={() => openServiceModal()}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-blue-700"
              >
                <Plus className="w-4 h-4" />
                Nuovo Servizio
              </button>
            </div>
            
            {loadingServices ? (
              <div className="p-8 text-center text-gray-500">Caricamento...</div>
            ) : services.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                Nessun servizio. Clicca "Nuovo Servizio" per iniziare.
              </div>
            ) : (
              <div className="divide-y">
                {services.map((service) => (
                  <div key={service.id} className="p-4 flex items-center gap-4 hover:bg-gray-50">
                    <div className="w-16 h-16 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                      {service.image_url ? (
                        <img
                          src={service.image_url.startsWith('/api') ? `${API_URL}${service.image_url}` : service.image_url}
                          alt={service.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <ImageIcon className="w-6 h-6 text-gray-400" />
                        </div>
                      )}
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="font-medium text-gray-900 truncate">{service.name}</h3>
                        {service.category && (
                          <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">
                            {service.category}
                          </span>
                        )}
                        {!service.is_active && (
                          <span className="text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded">
                            Disattivo
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-500 truncate">{service.short_description || service.description}</p>
                    </div>
                    
                    <div className="text-right flex-shrink-0">
                      <div className="font-semibold text-gray-900">€{service.price?.toFixed(2)}</div>
                      <div className="text-xs text-gray-500">IVA esclusa</div>
                    </div>
                    
                    <div className="flex gap-2 flex-shrink-0">
                      <button
                        onClick={() => openServiceModal(service)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => deleteService(service.id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Packages Tab */}
        {activeTab === 'packages' && (
          <div className="bg-white rounded-lg shadow">
            <div className="p-4 border-b flex justify-between items-center">
              <h2 className="font-semibold text-gray-800">Elenco Pacchetti</h2>
              <button
                onClick={() => openPackageModal()}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-blue-700"
              >
                <Plus className="w-4 h-4" />
                Nuovo Pacchetto
              </button>
            </div>
            
            {loadingPackages ? (
              <div className="p-8 text-center text-gray-500">Caricamento...</div>
            ) : packages.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                Nessun pacchetto. Clicca "Nuovo Pacchetto" per iniziare.
              </div>
            ) : (
              <div className="grid md:grid-cols-3 gap-4 p-4">
                {packages.map((pkg) => (
                  <div 
                    key={pkg.id} 
                    className={`border-2 rounded-lg p-4 relative ${
                      pkg.highlight ? 'border-blue-500 shadow-lg' : 'border-gray-200'
                    }`}
                    style={{ borderColor: pkg.highlight ? pkg.color : undefined }}
                  >
                    {pkg.badge && (
                      <div 
                        className="absolute -top-3 left-1/2 transform -translate-x-1/2 px-3 py-1 rounded-full text-white text-xs font-semibold"
                        style={{ backgroundColor: pkg.color }}
                      >
                        {pkg.badge}
                      </div>
                    )}
                    
                    <div className="text-center mb-4 mt-2">
                      <h3 className="font-bold text-lg text-gray-900">{pkg.name}</h3>
                      <p className="text-sm text-gray-500">{pkg.description}</p>
                    </div>
                    
                    <div className="text-center mb-4">
                      {pkg.original_price && (
                        <span className="text-gray-400 line-through text-sm mr-2">
                          €{pkg.original_price.toFixed(2)}
                        </span>
                      )}
                      <span className="text-2xl font-bold" style={{ color: pkg.color }}>
                        €{pkg.price?.toFixed(2)}
                      </span>
                      <span className="text-gray-500 text-sm">/IVA escl.</span>
                    </div>
                    
                    <div className="space-y-2 mb-4">
                      {pkg.services?.slice(0, 5).map((s, idx) => (
                        <div key={idx} className="flex items-center gap-2 text-sm">
                          {s.included ? (
                            <CheckCircle className="w-4 h-4 text-green-500" />
                          ) : (
                            <XCircle className="w-4 h-4 text-gray-300" />
                          )}
                          <span className={s.included ? 'text-gray-700' : 'text-gray-400'}>
                            {s.service_name}
                          </span>
                        </div>
                      ))}
                      {pkg.services?.length > 5 && (
                        <div className="text-sm text-gray-500 text-center">
                          +{pkg.services.length - 5} altri servizi
                        </div>
                      )}
                    </div>
                    
                    <div className="flex gap-2 pt-2 border-t">
                      <button
                        onClick={() => openPackageModal(pkg)}
                        className="flex-1 py-2 text-blue-600 hover:bg-blue-50 rounded flex items-center justify-center gap-1"
                      >
                        <Edit2 className="w-4 h-4" />
                        Modifica
                      </button>
                      <button
                        onClick={() => deletePackage(pkg.id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    
                    {!pkg.is_active && (
                      <div className="absolute top-2 right-2 bg-red-100 text-red-600 text-xs px-2 py-1 rounded">
                        Disattivo
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Service Modal */}
        {showServiceModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-4 border-b flex justify-between items-center sticky top-0 bg-white">
                <h2 className="text-lg font-semibold">
                  {editingService ? 'Modifica Servizio' : 'Nuovo Servizio'}
                </h2>
                <button onClick={() => setShowServiceModal(false)} className="p-2 hover:bg-gray-100 rounded">
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="p-4 space-y-4">
                {/* Image Upload */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Immagine</label>
                  <div className="flex items-center gap-4">
                    <div className="w-24 h-24 bg-gray-100 rounded-lg overflow-hidden">
                      {serviceForm.image_url ? (
                        <img
                          src={serviceForm.image_url.startsWith('/api') ? `${API_URL}${serviceForm.image_url}` : serviceForm.image_url}
                          alt="Preview"
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <ImageIcon className="w-8 h-8 text-gray-400" />
                        </div>
                      )}
                    </div>
                    <div>
                      <label className="cursor-pointer bg-gray-100 hover:bg-gray-200 px-4 py-2 rounded-lg inline-flex items-center gap-2">
                        <Upload className="w-4 h-4" />
                        {uploading ? 'Caricamento...' : 'Carica Immagine'}
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleImageUpload}
                          className="hidden"
                          disabled={uploading}
                        />
                      </label>
                    </div>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Nome *</label>
                    <input
                      type="text"
                      value={serviceForm.name}
                      onChange={(e) => setServiceForm(prev => ({ ...prev, name: e.target.value }))}
                      className="w-full border rounded-lg px-3 py-2"
                      placeholder="es: Registrazione Marchio"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Prezzo (€) IVA escl. *</label>
                    <input
                      type="number"
                      step="0.01"
                      value={serviceForm.price}
                      onChange={(e) => setServiceForm(prev => ({ ...prev, price: e.target.value }))}
                      className="w-full border rounded-lg px-3 py-2"
                      placeholder="es: 299.00"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Descrizione Breve</label>
                  <input
                    type="text"
                    value={serviceForm.short_description}
                    onChange={(e) => setServiceForm(prev => ({ ...prev, short_description: e.target.value }))}
                    className="w-full border rounded-lg px-3 py-2"
                    placeholder="Breve descrizione per tabelle pricing"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Descrizione Completa</label>
                  <textarea
                    value={serviceForm.description}
                    onChange={(e) => setServiceForm(prev => ({ ...prev, description: e.target.value }))}
                    className="w-full border rounded-lg px-3 py-2"
                    rows={3}
                    placeholder="Descrizione dettagliata del servizio"
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Categoria</label>
                    <select
                      value={serviceForm.category}
                      onChange={(e) => setServiceForm(prev => ({ ...prev, category: e.target.value }))}
                      className="w-full border rounded-lg px-3 py-2"
                    >
                      <option value="">Seleziona categoria</option>
                      {categories.map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Ordine</label>
                    <input
                      type="number"
                      value={serviceForm.sort_order}
                      onChange={(e) => setServiceForm(prev => ({ ...prev, sort_order: parseInt(e.target.value) || 0 }))}
                      className="w-full border rounded-lg px-3 py-2"
                    />
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="service_active"
                    checked={serviceForm.is_active}
                    onChange={(e) => setServiceForm(prev => ({ ...prev, is_active: e.target.checked }))}
                    className="w-4 h-4"
                  />
                  <label htmlFor="service_active" className="text-sm text-gray-700">Servizio attivo</label>
                </div>
              </div>

              <div className="p-4 border-t flex justify-end gap-2">
                <button
                  onClick={() => setShowServiceModal(false)}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
                >
                  Annulla
                </button>
                <button
                  onClick={saveService}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
                >
                  <Save className="w-4 h-4" />
                  Salva
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Package Modal */}
        {showPackageModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-4 border-b flex justify-between items-center sticky top-0 bg-white">
                <h2 className="text-lg font-semibold">
                  {editingPackage ? 'Modifica Pacchetto' : 'Nuovo Pacchetto'}
                </h2>
                <button onClick={() => setShowPackageModal(false)} className="p-2 hover:bg-gray-100 rounded">
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="p-4 space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Nome Pacchetto *</label>
                    <input
                      type="text"
                      value={packageForm.name}
                      onChange={(e) => setPackageForm(prev => ({ ...prev, name: e.target.value }))}
                      className="w-full border rounded-lg px-3 py-2"
                      placeholder="es: Starter, Business, Premium"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Badge (opzionale)</label>
                    <input
                      type="text"
                      value={packageForm.badge}
                      onChange={(e) => setPackageForm(prev => ({ ...prev, badge: e.target.value }))}
                      className="w-full border rounded-lg px-3 py-2"
                      placeholder="es: Più Popolare"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Descrizione</label>
                  <textarea
                    value={packageForm.description}
                    onChange={(e) => setPackageForm(prev => ({ ...prev, description: e.target.value }))}
                    className="w-full border rounded-lg px-3 py-2"
                    rows={2}
                    placeholder="Breve descrizione del pacchetto"
                  />
                </div>

                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Prezzo (€) *</label>
                    <input
                      type="number"
                      step="0.01"
                      value={packageForm.price}
                      onChange={(e) => setPackageForm(prev => ({ ...prev, price: e.target.value }))}
                      className="w-full border rounded-lg px-3 py-2"
                      placeholder="es: 499.00"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Prezzo Originale</label>
                    <input
                      type="number"
                      step="0.01"
                      value={packageForm.original_price}
                      onChange={(e) => setPackageForm(prev => ({ ...prev, original_price: e.target.value }))}
                      className="w-full border rounded-lg px-3 py-2"
                      placeholder="Prezzo barrato"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Ordine</label>
                    <input
                      type="number"
                      value={packageForm.sort_order}
                      onChange={(e) => setPackageForm(prev => ({ ...prev, sort_order: parseInt(e.target.value) || 0 }))}
                      className="w-full border rounded-lg px-3 py-2"
                      placeholder="1, 2, 3..."
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Colore Tema</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={packageForm.color}
                        onChange={(e) => setPackageForm(prev => ({ ...prev, color: e.target.value }))}
                        className="w-10 h-10 rounded cursor-pointer"
                      />
                      <input
                        type="text"
                        value={packageForm.color}
                        onChange={(e) => setPackageForm(prev => ({ ...prev, color: e.target.value }))}
                        className="flex-1 border rounded-lg px-3 py-2"
                      />
                    </div>
                  </div>
                  <div className="flex items-end gap-4">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={packageForm.highlight}
                        onChange={(e) => setPackageForm(prev => ({ ...prev, highlight: e.target.checked }))}
                        className="w-4 h-4"
                      />
                      <span className="text-sm text-gray-700">In evidenza</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={packageForm.is_active}
                        onChange={(e) => setPackageForm(prev => ({ ...prev, is_active: e.target.checked }))}
                        className="w-4 h-4"
                      />
                      <span className="text-sm text-gray-700">Attivo</span>
                    </label>
                  </div>
                </div>

                {/* Services Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Servizi Inclusi
                  </label>
                  {services.length === 0 ? (
                    <div className="text-sm text-gray-500 p-4 bg-gray-50 rounded-lg">
                      Nessun servizio disponibile. Crea prima i servizi.
                    </div>
                  ) : (
                    <div className="border rounded-lg divide-y max-h-64 overflow-y-auto">
                      {services.map(service => (
                        <div key={service.id} className="p-3 flex items-center justify-between hover:bg-gray-50">
                          <div>
                            <div className="font-medium text-sm">{service.name}</div>
                            <div className="text-xs text-gray-500">€{service.price?.toFixed(2)}</div>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => toggleServiceInPackage(service, true)}
                              className={`p-2 rounded ${
                                getServiceInclusionStatus(service.id) 
                                  ? 'bg-green-100 text-green-600' 
                                  : 'bg-gray-100 text-gray-400 hover:bg-green-50'
                              }`}
                            >
                              <CheckCircle className="w-4 h-4" />
                            </button>
                            <button
                              type="button"
                              onClick={() => toggleServiceInPackage(service, false)}
                              className={`p-2 rounded ${
                                !getServiceInclusionStatus(service.id) && packageForm.services.find(s => s.service_id === service.id)
                                  ? 'bg-red-100 text-red-600' 
                                  : 'bg-gray-100 text-gray-400 hover:bg-red-50'
                              }`}
                            >
                              <XCircle className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="p-4 border-t flex justify-end gap-2">
                <button
                  onClick={() => setShowPackageModal(false)}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
                >
                  Annulla
                </button>
                <button
                  onClick={savePackage}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
                >
                  <Save className="w-4 h-4" />
                  Salva
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminDigitalServices;
